import { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as REST from '~services/rest';
import { useAuth0 } from '@auth0/auth0-react';

import { AuthContext } from '~contexts/auth';
import { UserContext } from '~contexts/user';

import PaymentActions from '~redux/payment';
import IntakeActions from '~redux/intake';

import { useSearchParams } from '~hooks/common';

const useOpenChat = ({ personId, accountType, locationId }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const searchParams = useSearchParams('on-open-chat');

  const { getAccessTokenSilently } = useAuth0();
  const { isAuthenticated, signIn, signUp } = useContext(AuthContext);

  const { user } = useContext(UserContext);
  const { payedWorkers } = useSelector((state) => state.payment);

  const redirect = () => {
    history.push({ pathname: `/discussion/${personId}` });
  };

  const hasPayedBefore = () =>
    payedWorkers?.some((payed) => payed.customerId === user.personId && payed.workerId === personId);

  // Returns whether the chat may be opened, plus the backend's reason when it may
  // not (no_subscription, limit_reached, subscription_expired, wrong_location).
  // A null reason means the call itself failed, which the payment modal shows as a
  // retryable error rather than asking for payment.
  const fetchIsAllowedToChat = async () => {
    try {
      const token = await getAccessTokenSilently();
      const result = await REST.get({
        name: `payments`,
        shouldThrowError: true,
        mockFileName: 'payments/can-chat',
        params: {
          'customer-id': user.personId,
          'worker-id': personId,
        },
        token,
      });
      if (typeof result?.data?.allowed_to_talk !== 'boolean') {
        throw new Error('Invalid chat entitlement response');
      }
      return { allowed: result.data.allowed_to_talk, reason: result.data.reason || null, failed: false };
    } catch (err) {
      console.log('Error while fetching subscription details', err);
      return { allowed: false, reason: null, failed: true };
    }
  };

  const openChat = async () => {
    // If not authenticated goto register/login
    if (!isAuthenticated) {
      dispatch(IntakeActions.setReferrer(`/discussion/${personId}`));
      if (searchParams['on-open-chat']) {
        signIn();
        return;
      }
      signUp();
      return;
    }

    // If worker opening clients profile redirect to discussion
    if (accountType === 'client') {
      redirect();
    }

    if (hasPayedBefore()) {
      redirect();
      return;
    }

    const { allowed, reason, failed } = await fetchIsAllowedToChat();
    if (failed) return;
    const intakeLocationId = user?.wish?.address?.place_id || locationId;
    if (!allowed) {
      dispatch(PaymentActions.setModalData(personId, intakeLocationId, reason));
      dispatch(PaymentActions.toggleRequestModal(true));
      return;
    }
    dispatch(PaymentActions.addPayedWorker(user.personId, personId));
    redirect();
  };

  // Resolves to { allowed, reason }; reason is null whenever chatting is allowed
  // or the check could not be made.
  const canOpenChat = async ({ pId, aType }) => {
    // If not authenticated goto register
    if (!isAuthenticated) {
      dispatch(IntakeActions.setReferrer(`/discussion/${pId}`));
      signUp();
      return { allowed: false, reason: null };
    }

    // If worker opening clients profile redirect to discussion
    if (aType === 'client') {
      return { allowed: true, reason: null };
    }

    if (hasPayedBefore()) {
      return { allowed: true, reason: null };
    }

    const { allowed, reason, failed } = await fetchIsAllowedToChat();
    if (failed) return { allowed: false, reason: null, failed: true };
    if (!allowed) {
      dispatch(PaymentActions.setModalData(pId, locationId, reason));
      dispatch(PaymentActions.toggleRequestModal(true));
      return { allowed: false, reason };
    }
    dispatch(PaymentActions.addPayedWorker(user.personId, personId));
    return { allowed: true, reason: null };
  };

  return { openChat, canOpenChat };
};

export { useOpenChat };
